// The HQ console's navigation (spec/14 §1), in its order: Live · Leads · Organisations · Venues ·
// Subscriptions · Payments · Catalogue · Content · Loyalty · Promotions · Players · Reports ·
// Broadcasts · Fleet · Support · Platform settings · HQ team · Audit log.
//
// **Only what is built, and only to a role that may use it** (#475 rule 4). A section is shown when it
// has a route (`to`) and the signed-in role holds at least one of its permissions — the platform
// permissions of `GET /v1/me/access`, which are the HQ routes' own checks (spec/07 §7). A section with
// a route and no permission is never shown: it fails closed. When a screen lands, it gives its section
// a route here and its permissions from the routes it calls.
//
// The permissions are written as codes rather than imported from `@clavian/contracts`, which the
// console's bundle does not carry; `src/__tests__/hq.test.tsx` checks each is one the contract has.

import { HQ_PATHS } from './paths';

export const HQ_SECTION_IDS = [
  'live',
  'leads',
  'organizations',
  'venues',
  'subscriptions',
  'payments',
  'catalogue',
  'content',
  'loyalty',
  'promotions',
  'players',
  'reports',
  'broadcasts',
  'fleet',
  'support',
  'settings',
  'team',
  'audit',
] as const;

export type HqSectionId = (typeof HQ_SECTION_IDS)[number];

// The sidebar group a section sits in (design guide's grouped nav; labels are in `shell.group.*`).
// Two order-preserving buckets: everything the Provider *oversees* across the platform, then how the
// platform itself is *set up*. Every operational section (before Platform settings in spec/14 §1's
// order) is `oversight`, and settings/team/audit are `platform`, so the grouped order the sidebar
// draws never diverges from spec/14 §1 for any subset of built sections.
export type HqGroupId = 'oversight' | 'platform';

/** The groups in the order the grouped sidebar draws them (`HqLayout`). */
export const HQ_GROUP_ORDER: readonly HqGroupId[] = ['oversight', 'platform'];

export interface HqSection {
  readonly id: HqSectionId;
  /** Its first page, once the screen is built. A section without one is not shown. */
  readonly to?: string;
  /** Shown to a role holding any of these. Where a section's screen is not built, what its routes gate today. */
  readonly permissions: readonly string[];
  /** Which sidebar group it belongs to. */
  readonly group: HqGroupId;
  /** Whether a path is one of this section's pages. */
  readonly owns?: (pathname: string) => boolean;
}

/** `hq:settings` gates every platform settings route and the brand's (spec/14 §14). */
export const HQ_SETTINGS = 'hq:settings';

/** `hq:leads` gates the sales pipeline: the leads, their timeline, the templates and the funnel (spec/14 §16, F-HQ-39). */
export const HQ_LEADS = 'hq:leads';

/** `hq:organizations` gates the organisations' records, their owners' invitations, and every venue (spec/14 §3). */
export const HQ_ORGANIZATIONS = 'hq:organizations';
export const HQ_PAYMENTS = 'hq:payments';

/** `hq:subscriptions` gates the add-ons: the catalogue and one organisation's (spec/14 §4.5, F-BOOT-17). */
export const HQ_SUBSCRIPTIONS = 'hq:subscriptions';

/** `hq:catalog` gates the platform game catalogue: the search, the mirror and the list (spec/14 §6). */
export const HQ_CATALOG = 'hq:catalog';

/** `hq:players_read` gates the platform player search and one player's record (spec/14 §10). */
export const HQ_PLAYERS_READ = 'hq:players_read';

/** `hq:players` gates the platform ban and its lifting: `hq_owner`'s alone (spec/07 §3.5). */
export const HQ_PLAYERS = 'hq:players';

/** `hq:releases` gates station releases, their staged rollouts and the fleet's versions (spec/14 §12, F-STN-12). */
export const HQ_RELEASES = 'hq:releases';

/** `audit:read` at platform scope gates every organisation's audit log and the platform's (spec/07 §11.2). */
export const AUDIT_READ = 'audit:read';

export const HQ_SECTIONS: readonly HqSection[] = [
  // HQ Live (spec/14 §2, F-ANL-02): aggregate counters across every organisation — `hq:organizations`, which reads
  // the organisations and their venues already, gates it.
  { id: 'live', to: HQ_PATHS.live, permissions: [HQ_ORGANIZATIONS], group: 'oversight', owns: (pathname) => pathname === '/live' },
  // The sales pipeline (spec/14 §16, F-HQ-39): Today, the Pipeline, the Funnel and one lead — `hq:leads` gates every route they call.
  {
    id: 'leads',
    to: HQ_PATHS.leads,
    permissions: [HQ_LEADS],
    group: 'oversight',
    owns: (pathname) => pathname === '/leads' || pathname.startsWith('/leads/'),
  },
  // The organisations and venues area (spec/14 §3.1, §3.3): `hq:organizations` gates every route
  // both screens call. One organisation's settings is Platform settings' page, not this section's.
  {
    id: 'organizations',
    to: HQ_PATHS.organizations,
    permissions: [HQ_ORGANIZATIONS],
    group: 'oversight',
    owns: (pathname) => pathname === '/organizations' || /^\/organizations\/[^/]+(\/migration(\/[^/]+)?)?\/?$/.test(pathname),
  },
  { id: 'venues', to: HQ_PATHS.venues, permissions: [HQ_ORGANIZATIONS], group: 'oversight', owns: (pathname) => pathname === '/venues' },
  // Add-ons (spec/14 §4.5, F-BOOT-17): the catalogue and the price of each — `hq:subscriptions` gates every route it calls.
  { id: 'subscriptions', to: HQ_PATHS.subscriptions, permissions: [HQ_SUBSCRIPTIONS], group: 'oversight', owns: (pathname) => pathname === '/subscriptions' },
  { id: 'payments', permissions: [], group: 'oversight' },
  // The platform game catalogue (spec/14 §6, F-CAT-01, F-CAT-07): `hq:catalog` gates the search, the
  // mirror and the list — the routes the screen calls.
  { id: 'catalogue', to: HQ_PATHS.catalogue, permissions: [HQ_CATALOG], group: 'oversight', owns: (pathname) => pathname === '/catalogue' },
  { id: 'content', permissions: [], group: 'oversight' },
  { id: 'loyalty', permissions: [], group: 'oversight' },
  { id: 'promotions', permissions: [], group: 'oversight' },
  // Looking a person up and opening their platform record (spec/14 §10). The ban (`hq:players`) is an
  // action on a record this opens, so a role that cannot read one has nothing to ban from here.
  { id: 'players', to: HQ_PATHS.players, permissions: [HQ_PLAYERS_READ], group: 'oversight' },
  { id: 'reports', permissions: [], group: 'oversight' },
  { id: 'broadcasts', permissions: [], group: 'oversight' },
  // Station clients (spec/14 §12, F-STN-12): the releases, each channel's rollout and the fleet's versions —
  // `hq:releases` gates every route the screen calls.
  { id: 'fleet', to: HQ_PATHS.fleet, permissions: [HQ_RELEASES], group: 'oversight', owns: (pathname) => pathname === '/fleet' },
  { id: 'support', permissions: [], group: 'oversight' },
  {
    id: 'settings',
    to: HQ_PATHS.settings,
    permissions: [HQ_SETTINGS],
    group: 'platform',
    // Platform defaults, the brand, the providers (F-HQ-38) and one organisation's settings (spec/14 §14, §15).
    owns: (pathname) =>
      pathname === '/settings' ||
      pathname.startsWith('/settings/') ||
      pathname === '/brand' ||
      pathname === '/providers' ||
      /^\/organizations\/[^/]+\/settings(\/|$)/.test(pathname),
  },
  { id: 'team', permissions: [], group: 'platform' },
  // Every organisation's audit log and the platform's, read with a reason (spec/07 §11.2, spec/14 §15).
  { id: 'audit', to: HQ_PATHS.audit, permissions: [AUDIT_READ], group: 'platform' },
];

/** The sections to show a role with these permissions, in spec/14 §1's order. */
export function visibleSections(permissions: ReadonlySet<string>): readonly (HqSection & { readonly to: string })[] {
  return HQ_SECTIONS.filter(
    (section): section is HqSection & { readonly to: string } =>
      section.to !== undefined && section.permissions.some((permission) => permissions.has(permission)),
  );
}
